import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRightIcon } from '@heroicons/react/24/outline';
import { 
  MapIcon, 
  HeartIcon, 
  UsersIcon,
  SparklesIcon
} from '@heroicons/react/24/solid';
// Import Swiper components and modules
import { Swiper, SwiperSlide } from 'swiper/react';
import { Autoplay, Navigation, Pagination, EffectFade } from 'swiper/modules';
// Import Swiper styles
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import 'swiper/css/effect-fade';
// Import motion - removed unused imports
// Import BookingModal
import BookingModal from '../components/BookingModal';
// Import Testimonials component
import Testimonials from '../components/Testimonials';
import { HeroVideoBackground } from '../components/HeroVideo';
import VerticalVideoCarousel from '../components/VerticalVideoCarousel';
import FindUsWorldwide from '../components/FindUsWorldwide';
import GalleryPreview from '../components/GalleryPreview';
import useSEO from '../hooks/useSEO';
// Import gallery constants
// import galleryData from '../constants/gallery.json';

export default function Home() {
  // State for booking modal
  const [isBookingModalOpen, setIsBookingModalOpen] = useState(false);
  
  // State for scroll animations
  const [visibleElements, setVisibleElements] = useState<Set<string>>(new Set());// Refs for animated elements
  const heroRef = useRef<HTMLDivElement>(null);
  const carouselRef = useRef<HTMLDivElement>(null);
  const videoCarouselRef = useRef<HTMLDivElement>(null);
  const aboutRef = useRef<HTMLDivElement>(null);
  const featuresRef = useRef<HTMLDivElement>(null);
  const galleryRef = useRef<HTMLDivElement>(null);
  const testimonialsRef = useRef<HTMLDivElement>(null);
  const ctaRef = useRef<HTMLDivElement>(null);
  const platformsRef = useRef<HTMLDivElement>(null);  const openBookingModal = () => setIsBookingModalOpen(true);
  const closeBookingModal = () => setIsBookingModalOpen(false);

  // Intersection Observer for scroll animations
  useEffect(() => {
    const observerOptions = {
      threshold: 0.15, // Increased threshold for more dramatic timing
      rootMargin: '0px 0px -100px 0px' // Larger margin for earlier triggering
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const elementId = entry.target.getAttribute('data-animate');
          if (elementId) {
            setVisibleElements(prev => new Set(prev).add(elementId));
          }
        }
      });
    }, observerOptions);    // Observe elements - including new sections
    const elementsToObserve = [
      carouselRef, 
      videoCarouselRef, 
      heroRef, 
      aboutRef, 
      featuresRef, 
      galleryRef, 
      testimonialsRef, 
      ctaRef,
      platformsRef
    ];
    elementsToObserve.forEach(ref => {
      if (ref.current) {
        observer.observe(ref.current);
      }
    });
      return () => observer.disconnect();
  }, []);

  const features = [
    {
      name: 'Mountain Views',
      description: 'Wake up to breathtaking views of the Himalayan foothills',
      icon: MapIcon,
    },
    {
      name: 'Authentic Experience',
      description: 'Live like a local and experience traditional Nepali culture',
      icon: HeartIcon,
    },
    {
      name: 'Warm Hospitality',
      description: 'Feel at home with our welcoming family atmosphere',
      icon: UsersIcon,
    },
    {
      name: 'Peaceful Environment',
      description: 'Escape the city bustle in our serene natural setting',
      icon: SparklesIcon,
    },
  ];  // Carousel images with local Bethanchok homestay images
  const carouselImages = [
    {
      id: 1,
      src: '/images/carousel/1.jpg',
      alt: 'Bethanchok Homestay exterior with traditional architecture',
      title: 'Welcome to Bethanchok Homestay',
      subtitle: 'Your home away from home in the heart of Nepal'
    },    {
      id: 2,
      src: '/images/carousel/2.jpg',
      alt: 'Scenic hiking trails and mountain paths near Bethanchok',
      title: 'Adventure Hiking Trails',
      subtitle: 'Explore pristine mountain trails and hidden valleys'
    },
    {
      id: 3,
      src: '/images/carousel/3.jpg',
      alt: 'Delicious traditional Nepali meals served fresh',
      title: 'Authentic Nepali Cuisine',
      subtitle: 'Taste homemade dal bhat and local specialties'
    },
    {
      id: 4,
      src: '/images/carousel/4.jpg',
      alt: 'Stunning mountain and valley views from Bethanchok',
      title: 'Spectacular Natural Beauty',
      subtitle: 'Breathtaking views of the Himalayan landscape'
    },
    {
      id: 5,
      src: '/images/carousel/5.jpg',
      alt: 'Cultural activities and village life experiences',
      title: 'Immersive Cultural Experience',
      subtitle: 'Connect with local traditions and community life'
    }  ];

  const homeStructuredData = {
    "@context": "https://schema.org",
    "@type": "LodgingBusiness",
    "name": "Bethanchok Homestay",
    "image": [
      "https://bethanchokstay.com/images/bethanchok-logo.png",
      "https://bethanchokstay.com/images/carousel/1.jpg",
      "https://bethanchokstay.com/images/carousel/2.jpg"
    ],
    "description": "Authentic Nepali homestay experience in traditional village setting with mountain views, cultural activities, yoga, meditation, and trekking adventures.",
    "url": "https://bethanchokstay.com",
    "address": {
      "@type": "PostalAddress",
      "addressLocality": "Bethanchok",
      "addressRegion": "Central Nepal",
      "addressCountry": "Nepal"
    },
    "geo": {
      "@type": "GeoCoordinates",
      "latitude": 27.8,
      "longitude": 85.6
    },
    "amenityFeature": [
      {"@type": "LocationFeatureSpecification", "name": "Mountain Views"},
      {"@type": "LocationFeatureSpecification", "name": "Traditional Accommodation"},
      {"@type": "LocationFeatureSpecification", "name": "Cultural Experiences"},
      {"@type": "LocationFeatureSpecification", "name": "Yoga and Meditation"},
      {"@type": "LocationFeatureSpecification", "name": "Trekking Activities"},
      {"@type": "LocationFeatureSpecification", "name": "Organic Farm Experience"},
      {"@type": "LocationFeatureSpecification", "name": "Peaceful Environment"}
    ],
    "starRating": {
      "@type": "Rating",
      "ratingValue": "5"
    },
    "priceRange": "$$",
    "hasOfferCatalog": {
      "@type": "OfferCatalog",
      "name": "Homestay Services",
      "itemListElement": [
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Traditional Room Stay"
          }
        },
        {
          "@type": "Offer", 
          "itemOffered": {
            "@type": "Service",
            "name": "Cultural Experience Tours"
          }
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service", 
            "name": "Yoga and Meditation Sessions"
          }
        }
      ]
    }  };

  // SEO Hook
  useSEO({
    title: "Bethanchok Homestay | Best Nepal Homestay Experience | Traditional Village Stay in Nepal",
    description: "Experience authentic Nepali hospitality at Bethanchok Homestay. Traditional village accommodation in Nepal with stunning mountain views, cultural experiences, yoga, meditation, and trekking. Book your authentic Nepal homestay today!",
    keywords: "Nepal homestay, Bethanchok homestay, Nepal tourism, traditional Nepal accommodation, Nepal village stay, authentic Nepal experience, Nepal cultural tourism, mountain views Nepal, trekking Nepal, yoga retreat Nepal, meditation Nepal, Nepal adventure tourism, sustainable tourism Nepal, eco-friendly Nepal stay, Nepal rural tourism, Himalayan homestay, Nepal eco lodge, traditional Nepali culture, Nepal mountain views, authentic Nepali experience",
    image: "https://bethanchokstay.com/images/bethanchok-logo.png",
    url: "https://bethanchokstay.com/",
    structuredData: homeStructuredData
  });

  return (
    <div className="bg-white page-transition">
      {/* Hero Carousel section */}
      <div 
        ref={carouselRef}
        data-animate="carousel"
        className={`relative transition-all duration-1000 ease-out ${
          visibleElements.has('carousel')
            ? 'opacity-100 scale-100'
            : 'opacity-0 scale-95'
        }`}
      ><Swiper
          modules={[Autoplay, Navigation, Pagination, EffectFade]}
          effect="fade"
          spaceBetween={0}
          slidesPerView={1}
          speed={1500}
          autoplay={{
            delay: 5000,
            disableOnInteraction: false,
          }}
          navigation={true}
          pagination={{
            clickable: true,
            dynamicBullets: false,
          }}
          className="h-[85vh] w-full homepage-carousel"
        >          {carouselImages.map((image, index) => {
            // Define different animation patterns for each slide
            const getAnimationClass = (slideIndex: number, elementType: 'title' | 'subtitle' | 'button') => {
              const patterns = [
                { title: 'animate-slide-in-up', subtitle: 'animate-slide-in-up', button: 'animate-slide-in-up' },
                { title: 'animate-slide-in-left', subtitle: 'animate-slide-in-right', button: 'animate-slide-in-up' },
                { title: 'animate-slide-in-right', subtitle: 'animate-slide-in-left', button: 'animate-slide-in-up' },
                { title: 'animate-slide-in-up', subtitle: 'animate-slide-in-left', button: 'animate-slide-in-right' },
                { title: 'animate-slide-in-left', subtitle: 'animate-slide-in-up', button: 'animate-slide-in-left' }
              ];
              return patterns[slideIndex % patterns.length][elementType];
            };

            return (
              <SwiperSlide key={image.id} className="relative">
                <div className="absolute inset-0 bg-black/30 z-10" />
                {/* Background with fallback color */}
                <div 
                  className="h-full w-full bg-cover bg-center"
                  style={{ 
                    backgroundColor: '#2d3748', // A dark slate color as fallback
                    backgroundImage: `url(${image.src})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center'
                  }}
                >
                  {/* Static fallback gradient that will show if image fails to load */}
                  <div className="absolute inset-0 bg-gradient-to-tr from-primary-600/40 to-secondary-600/40 z-5" />
                </div>
                <div className="absolute inset-0 z-20 flex flex-col items-center justify-center text-center px-4 sm:px-6 lg:px-8">
                  <h2 className={`text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-5 ${getAnimationClass(index, 'title')}`}>
                    {image.title}
                  </h2>
                  <p className={`text-xl sm:text-2xl lg:text-3xl text-white max-w-3xl mb-2 ${getAnimationClass(index, 'subtitle')} animation-delay-200`}>
                    {image.subtitle}
                  </p>                
                  <div className={`mt-10 ${getAnimationClass(index, 'button')} animation-delay-400`}>
                    <button
                      onClick={openBookingModal}
                      className="rounded-lg bg-primary-600 px-8 py-4 text-base md:text-lg font-semibold text-white shadow-md hover:bg-primary-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-600 transition-all duration-300 hover:shadow-lg"
                    >
                      Book Your Stay
                    </button>
                  </div>
                </div>
              </SwiperSlide>
            );
          })}
        </Swiper>      </div>      {/* Hero section with Video Background */}
      <div 
        ref={videoCarouselRef}
        data-animate="videoCarousel"
        className={`transition-all duration-1000 ease-out transform ${
          visibleElements.has('videoCarousel')
            ? 'opacity-100 translate-y-0 scale-100'
            : 'opacity-0 translate-y-20 scale-95'
        }`}
      >
        <VerticalVideoCarousel />
      </div>
      <HeroVideoBackground opacity={0.3}>        <div 
          ref={heroRef}
          data-animate="hero"
          className={`mx-auto max-w-2xl py-32 sm:py-48 lg:py-56 transition-all duration-1000 ease-out transform ${
            visibleElements.has('hero') 
              ? 'opacity-100 translate-y-0 scale-100' 
              : 'opacity-0 translate-y-16 scale-95'
          }`}
        >
          <div className="text-center">
            <h1 className={`text-4xl font-bold tracking-tight text-white sm:text-6xl transition-all duration-1000 delay-200 ease-out transform ${
              visibleElements.has('hero') 
                ? 'opacity-100 translate-y-0 scale-100' 
                : 'opacity-0 translate-y-12 scale-90'
            }`}>
              Welcome to{' '}
              <span className="text-primary-300">Bethanchok</span>{' '}
              Homestay
            </h1>
            <p className={`mt-6 text-lg leading-8 text-gray-200 transition-all duration-1000 delay-400 ease-out transform ${
              visibleElements.has('hero') 
                ? 'opacity-100 translate-y-0 scale-100' 
                : 'opacity-0 translate-y-8 scale-95'
            }`}>
              Discover the authentic beauty of Nepal in our peaceful village homestay. 
              Experience traditional culture, stunning mountain views, and warm hospitality 
              that will make your stay unforgettable.
            </p>            
            <div className={`mt-10 flex items-center justify-center gap-x-6 transition-all duration-1000 delay-600 ease-out transform ${
              visibleElements.has('hero') 
                ? 'opacity-100 translate-y-0 scale-100' 
                : 'opacity-0 translate-y-6 scale-90'
            }`}>
              <button
                onClick={openBookingModal}
                className="rounded-lg bg-primary-600 px-6 py-3 text-base font-semibold text-white shadow-sm hover:bg-primary-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-600 transition-colors duration-300"
              >
                Book Your Stay
              </button>
              <Link
                to="/rooms"
                className="text-base font-semibold leading-6 text-white hover:text-primary-300 transition-colors duration-200 flex items-center gap-2"
              >
                Explore Rooms <ArrowRightIcon className="h-5 w-5" />
              </Link>
            </div>
          </div>
        </div>
      </HeroVideoBackground>      {/* Modern Vertical Video Carousel Section */}

      {/* About section - visually enhanced */}
      <div className="relative py-24 sm:py-32 bg-gradient-to-br from-primary-50 via-white to-secondary-50 overflow-hidden">
        {/* Decorative background image for about section */}
        <div className="absolute inset-0 z-0">
          <img 
            src="https://images.unsplash.com/photo-1506905925346-21bda4d32df4?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80" 
            alt="Himalayan foothills" 
            className="w-full h-full object-cover object-center opacity-20" 
            loading="lazy"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-white/90 via-white/70 to-primary-50/80" />
        </div>
        <div className="relative z-10 mx-auto max-w-7xl px-6 lg:px-8">          <div 
            ref={aboutRef}
            data-animate="about"
            className={`mx-auto max-w-2xl lg:text-center transition-all duration-1000 ease-out transform ${
              visibleElements.has('about') 
                ? 'opacity-100 translate-y-0 scale-100' 
                : 'opacity-0 translate-y-16 scale-95'
            }`}
          >
            <h2 className={`text-base font-semibold leading-7 text-primary-600 mb-2 tracking-widest uppercase transition-all duration-1000 delay-200 ease-out ${
              visibleElements.has('about') 
                ? 'opacity-100 translate-y-0' 
                : 'opacity-0 translate-y-8'
            }`}>Experience Nepal</h2>
            <p className={`text-3xl font-extrabold tracking-tight text-neutral-900 sm:text-4xl mb-4 drop-shadow-lg transition-all duration-1000 delay-300 ease-out ${
              visibleElements.has('about') 
                ? 'opacity-100 translate-y-0' 
                : 'opacity-0 translate-y-8'
            }`}>Your home away from home</p>
            <p className={`mt-2 text-lg leading-8 text-neutral-700 font-medium transition-all duration-1000 delay-400 ease-out ${
              visibleElements.has('about') 
                ? 'opacity-100 translate-y-0' 
                : 'opacity-0 translate-y-8'
            }`}>
              Nestled in the heart of Bethanchok village, our homestay offers an authentic glimpse into Nepali rural life. Surrounded by terraced fields and rolling hills, you'll find peace and tranquility while experiencing genuine local hospitality.
            </p>
          </div>
          {/* Features visually as cards */}
          <div 
            ref={featuresRef}
            data-animate="features"
            className={`mx-auto mt-16 max-w-4xl grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-8 transition-all duration-1000 delay-500 ease-out transform ${
              visibleElements.has('features') 
                ? 'opacity-100 translate-y-0 scale-100' 
                : 'opacity-0 translate-y-20 scale-95'
            }`}
          >
            {features.map((feature, index) => (
              <div
                key={feature.name}
                className="relative flex flex-col items-center bg-white/90 rounded-2xl shadow-xl p-8 hover:shadow-2xl transition-all duration-300 border border-primary-100 backdrop-blur-md"
                style={{ transitionDelay: `${400 + index * 50}ms` }}
              >
                <div className="flex items-center justify-center w-16 h-16 rounded-full bg-primary-100 mb-4 shadow-md">
                  <feature.icon className="h-8 w-8 text-primary-600" aria-hidden="true" />
                </div>
                <h3 className="text-lg font-bold text-neutral-900 mb-2 text-center">{feature.name}</h3>
                <p className="text-base text-neutral-600 text-center">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>      </div>

      {/* Gallery Preview Section */}
      <GalleryPreview 
        visibleElements={visibleElements}
        galleryRef={galleryRef}
      />      {/* Testimonials Section */}
      <Testimonials 
        visibleElements={visibleElements}
        testimonialsRef={testimonialsRef}
      />      {/* Find us worldwide section */}
      <FindUsWorldwide 
        visibleElements={visibleElements}
        platformsRef={platformsRef}
      />

      {/* CTA section */}
      <div className="bg-primary-50">
        <div className="px-6 py-24 sm:px-6 sm:py-32 lg:px-8">          <div 
            ref={ctaRef}
            data-animate="cta"
            className={`mx-auto max-w-2xl text-center transition-all duration-1000 ease-out transform ${
              visibleElements.has('cta') 
                ? 'opacity-100 translate-y-0 scale-100' 
                : 'opacity-0 translate-y-16 scale-95'
            }`}
          >
            <h2 className={`text-3xl font-bold tracking-tight text-neutral-900 sm:text-4xl transition-all duration-1000 delay-200 ease-out transform ${
              visibleElements.has('cta') 
                ? 'opacity-100 translate-y-0 scale-100' 
                : 'opacity-0 translate-y-12 scale-90'
            }`}>
              Ready for an authentic experience?
            </h2>
            <p className={`mx-auto mt-6 max-w-xl text-lg leading-8 text-neutral-600 transition-all duration-1000 delay-400 ease-out transform ${
              visibleElements.has('cta') 
                ? 'opacity-100 translate-y-0 scale-100' 
                : 'opacity-0 translate-y-8 scale-95'
            }`}>
              Book your stay with us and discover the beauty of Nepal through the eyes of locals. 
              We can't wait to welcome you to our family.
            </p>            
            <div className={`mt-10 flex items-center justify-center gap-x-6 transition-all duration-1000 delay-600 ease-out transform ${
              visibleElements.has('cta') 
                ? 'opacity-100 translate-y-0 scale-100' 
                : 'opacity-0 translate-y-6 scale-90'
            }`}>
              <button
                onClick={openBookingModal}
                className="rounded-lg bg-primary-600 px-6 py-3 text-base font-semibold text-white shadow-sm hover:bg-primary-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-600 transition-colors duration-200"
              >
                Book Now
              </button>
              <Link
                to="/contact"
                className="text-base font-semibold leading-6 text-neutral-900 hover:text-primary-600 transition-colors duration-200"
              >
                Contact Us
              </Link>
            </div>
          </div>
        </div>      </div>

      {/* Image Attribution Disclaimer */}
      <div className="bg-neutral-50 py-6">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <p className="text-center text-xs text-neutral-500 leading-relaxed">
            <span className="font-medium">Image Disclaimer:</span> Some images on this website are sourced from multiple providers and stock photo services. 
            Bethanchok Homestay does not claim ownership of all images displayed. 
            All images are used for promotional and informational purposes only.
          </p>
        </div>
      </div>

      {/* Booking Modal */}
      <BookingModal isOpen={isBookingModalOpen} closeModal={closeBookingModal} />
    </div>
  );
}
